/**
 * DataTable Component
 * Standardized table with sorting, pagination, and search
 * Based on LifeScribe Design System v4.0
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Skeleton,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors, typography, spacing, shadows } from '../../theme/designTokens';
import EmptyState from './EmptyState';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface PaginationConfig {
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onRowClick?: (row: T) => void;
  pagination?: PaginationConfig;
  emptyMessage?: string;
  emptyIcon?: string;
  stickyHeader?: boolean;
}

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: shadows.sm,
  border: `1px solid ${colors.gray[200]}`,
  overflow: 'hidden',
});

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? colors.gray[50] : colors.gray[800],
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontFamily: typography.fonts.heading,
  fontWeight: typography.weights.semibold,
  fontSize: typography.sizes.sm,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: theme.palette.mode === 'light' ? colors.gray[700] : colors.gray[300],
  padding: spacing[4],
  borderBottom: `2px solid ${colors.gray[200]}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 150ms ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light' ? colors.deepTeal[50] : colors.gray[700],
  },
  '&:last-child td': {
    borderBottom: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: spacing[4],
  borderBottom: `1px solid ${
    theme.palette.mode === 'light' ? colors.gray[200] : colors.gray[700]
  }`,
  fontSize: typography.sizes.sm,
}));

const LoadingRow: React.FC<{ columns: number }> = ({ columns }) => (
  <TableRow>
    {Array.from({ length: columns }).map((_, index) => (
      <StyledTableCell key={index}>
        <Skeleton variant="text" width="100%" height={20} />
      </StyledTableCell>
    ))}
  </TableRow>
);

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  onSort,
  onRowClick,
  pagination,
  emptyMessage = 'No data available',
  emptyIcon = 'no_data',
  stickyHeader = true,
}: DataTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSortClick = (column: Column<T>) => {
    if (!column.sortable || !onSort) return;

    const isAsc = sortColumn === column.id && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';

    setSortColumn(column.id as string);
    setSortDirection(newDirection);
    onSort(column.id as string, newDirection);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    pagination?.onPageChange(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    pagination?.onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Show empty state when no data and not loading
  if (!loading && data.length === 0) {
    return (
      <StyledTableContainer component={Paper}>
        <Box sx={{ p: spacing[8] }}>
          <EmptyState
            icon={emptyIcon as any}
            title={emptyMessage}
            description="Try adjusting your filters or search criteria"
          />
        </Box>
      </StyledTableContainer>
    );
  }

  return (
    <Box>
      <StyledTableContainer component={Paper}>
        <Table stickyHeader={stickyHeader}>
          {/* Table Head */}
          <StyledTableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledHeaderCell
                  key={column.id as string}
                  align={column.align || 'left'}
                  style={{ width: column.width }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortColumn === column.id}
                      direction={sortColumn === column.id ? sortDirection : 'asc'}
                      onClick={() => handleSortClick(column)}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: `${colors.deepTeal[500]} !important`,
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </StyledHeaderCell>
              ))}
            </TableRow>
          </StyledTableHead>

          {/* Table Body */}
          <TableBody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, index) => (
                <LoadingRow key={index} columns={columns.length} />
              ))
            ) : (
              // Data rows
              data.map((row, rowIndex) => (
                <StyledTableRow
                  key={rowIndex}
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.id as string}
                      align={column.align || 'left'}
                    >
                      {column.render
                        ? column.render(row)
                        : (row[column.id as keyof T] as React.ReactNode)}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {pagination && !loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: spacing[4],
            backgroundColor: colors.gray[50],
            borderRadius: '12px',
            border: `1px solid ${colors.gray[200]}`,
          }}
        >
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: 48,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontFamily: typography.fonts.body,
                fontSize: typography.sizes.sm,
                fontWeight: typography.weights.medium,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default DataTable;
